/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {ChangeDetectionStrategy, Component, Inject, OnInit, signal} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';

import template from './target-super-summary-detail-modal.component.html';
import {OBJECT_TYPE, ObjectTypeFinder} from '../common/constants/object-type';
import {CreativeApiService} from '../creative/creative-api.service';
import {LineItemApiService} from '../line-item/line-item-api.service';
import {CreativeSetApiService} from '../creative-set/creative-set-api.service';

@Component({
  standalone: false,
  selector: 'adn-target-super-summary-detail-modal',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TargetSuperSummaryDetailModalComponent implements OnInit {
  /** Seeded via BsModalService initialState (plain fields, not signals). */
  targeting: any;
  obj: any;

  readonly searching = signal(false);
  readonly viewTargeting = signal<any>(undefined);
  readonly viewObj = signal<any>(undefined);

  constructor(
    public readonly bsModalRef: BsModalRef,
    @Inject(LineItemApiService) private readonly LineItem: LineItemApiService,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    private readonly creativeSetApi: CreativeSetApiService,
  ) {
  }

  ngOnInit(): void {
    this.viewTargeting.set(this.targeting);
    this.viewObj.set(this.obj);

    // Concise list payloads only include *Size counts (for whichever types are
    // present — not always adUnitTargetSize). Fetch the full object so
    // adn-target-summary can render the actual targeting details.
    if (this.targeting && !this.isConciseTargeting(this.targeting)) {
      return;
    }

    const objectType = this.resolveObjectType(this.obj);
    if (!objectType?.name || !this.obj?.id) {
      return;
    }

    this.searching.set(true);

    const successFunc = (result: any) => {
      this.targeting = result.targeting;
      this.obj = result;
      this.viewTargeting.set(result.targeting);
      this.viewObj.set(result);
      this.searching.set(false);
    };

    const errorFunc = () => {
      this.targeting = {};
      this.viewTargeting.set({});
      this.searching.set(false);
    };

    if (objectType.name === OBJECT_TYPE.LineItem.name) {
      firstValueFrom(this.LineItem.get(this.obj.id)).then(successFunc, errorFunc);
    } else if (objectType.name === OBJECT_TYPE.Creative.name) {
      firstValueFrom(this.Creative.get(this.obj.id)).then(successFunc, errorFunc);
    } else if (objectType.name === OBJECT_TYPE.CreativeSet.name) {
      this.creativeSetApi.get(this.obj.id).subscribe({next: successFunc, error: errorFunc});
    } else {
      this.searching.set(false);
    }
  }

  /** True when targeting looks like a concise list payload (*Size counts only). */
  private isConciseTargeting(targeting: any): boolean {
    return _.some(targeting, (value, key: string) => _.endsWith(key, 'Size') && _.isFinite(value));
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  /**
   * Resource instances retain their API type even when a concise row's `url`
   * identifies a related object. Fall back to metadata and shape heuristics.
   */
  private resolveObjectType(obj: any): any {
    const fromResource = _.find(OBJECT_TYPE, {apiParam: obj?.resourceName});
    if (fromResource?.name) {
      return fromResource;
    }

    const fromUrl = ObjectTypeFinder.findObjectType(obj);
    if (fromUrl?.name) {
      return fromUrl;
    }

    const typeKey = obj?.objectType;
    if (_.isString(typeKey)) {
      return OBJECT_TYPE[typeKey]
        || _.find(OBJECT_TYPE, {type: typeKey})
        || _.find(OBJECT_TYPE, {caps: typeKey})
        || {};
    }
    if (typeKey?.name) {
      return typeKey;
    }

    if (obj && (obj.layout || obj.type === 'INTERNAL' || obj.type === 'EXTERNAL' || obj.externalDemandSource)) {
      return OBJECT_TYPE.Creative;
    }
    if (obj?.folio) {
      return OBJECT_TYPE.CreativeSet;
    }
    if (obj && (obj.order || obj.bidSpecification || obj.objectives || obj.axProduct)) {
      return OBJECT_TYPE.LineItem;
    }

    return {};
  }
}
